'use client';

import { ReactNode, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { observer } from 'mobx-react-lite';
import ButtonShowMore from 'components/ButtonShowMore/ButtonShowMore';
import { SearchEventResult } from 'searchEvent.d';
import { SearchPlacesResult } from 'searchPlaces.d';
import { SearchProducersResult } from 'searchProducers.d';
import { LIMITS } from 'constants/index';
import Pagination from '@sympla/sympla-components/Pagination';
import SearchResultInfo from 'components/SearchResultInfo/SearchResultInfo';
import EmptySearchResults from 'components/EmptySearchResults/EmptySearchResults';
import { EMPTY_STATES } from 'constants/emptyStates';
import locationStore from 'stores/LocationStore';

const PaginationExtraLabel = dynamic(
  () => import('components/Shared/PaginationExtraLabel/PaginationExtraLabel'),
  {
    ssr: false
  }
);

type SearchContentProps = {
  children: ReactNode;
  pageTypeText: string;
  searchResult: SearchEventResult | SearchPlacesResult | SearchProducersResult;
  otherSearchResult?: SearchEventResult;
  totalPages: number;
  isMobile: boolean;
  isFetching?: boolean;
  onChangePage: (page: number | string) => number;
};

function SearchContent({
  children,
  pageTypeText,
  searchResult,
  otherSearchResult,
  totalPages,
  isFetching,
  onChangePage,
  isMobile
}: SearchContentProps) {
  const { data, page, total } = searchResult;

  const extraLabel = useMemo(
    () =>
      isMobile && LIMITS.MOBILE < total ? (
        <PaginationExtraLabel currentResults={data?.length} total={total} />
      ) : (
        <div />
      ),
    [data, total]
  );

  const showPagination =
    (!!data?.length && !otherSearchResult?.data?.length) ||
    (!data?.length && !!otherSearchResult?.data?.length);

  return (
    <>
      <SearchResultInfo pageTypeText={pageTypeText} numberOfEvents={total} />

      {data?.length || otherSearchResult?.data?.length ? (
        <>
          {!data?.length ? (
            <EmptySearchResults
              title={`${EMPTY_STATES.OTHER_CITY.title} ${locationStore?.selectedLocation?.city}`}
              subtitle={EMPTY_STATES.OTHER_CITY.subtitle}
            />
          ) : null}
          {children}
        </>
      ) : (
        <EmptySearchResults
          title={EMPTY_STATES.NO_RESULTS.title}
          subtitle={EMPTY_STATES.NO_RESULTS.subtitle}
          showRecommendation
        />
      )}

      {showPagination && totalPages > 1 && (
        <Pagination
          isLoading={isFetching}
          lang="pt"
          totalPages={totalPages}
          currentPage={Number(page) || otherSearchResult?.page || 1}
          onChangePage={onChangePage}
          extraLabel={extraLabel}
          isMobile={isMobile}
        />
      )}

      {!!data?.length &&
        !!otherSearchResult?.data?.length &&
        otherSearchResult?.data?.length >= LIMITS.EVENTS_OTHER_CITIES && (
          <ButtonShowMore />
        )}
    </>
  );
}

export default observer(SearchContent);
